import { useContext, useEffect, useRef, useState } from "react";
import { API_BASE } from "../config";
import { toast } from "../ui/Toast";
import { trackOnce } from "../track";
import { siteUrl } from "./chrome";
import { PageContext } from "./page";
import type { Words } from "./copy";
import type { PreviewDoc } from "../types";

/** The site's newsletter offer, on the page itself: join, and the 10% comes
 *  off this preview at once.
 *
 *  Previews sent from 6 October 2026 carry no code and are quoted the full
 *  price; the discount for joining was there all along, but only for somebody who
 *  found the site's sign-up box after asking for their preview. Nothing to
 *  type: the page sends its token and the server signs up the address the
 *  preview was sent to, which the page itself never holds. The tick is the
 *  site's own, in the site's own words, because the privacy page says consent
 *  is given by ticking the box and the list keeps the sentence that was ticked.
 *
 *  Offered only where the server says (preview.newsletter): a page holding no
 *  code whose lead could still be given one. After joining, the page is read
 *  again, so every price on it, and on their other previews, moves to the
 *  discounted one, and this card stays to say so for the rest of the visit.
 *  In the studio's look it is shown but sends nothing: the tick would be the
 *  owner's, not theirs. */
export default function NewsletterOffer({ preview, words: t }: { preview: PreviewDoc; words: Words }) {
  const page = useContext(PageContext);
  const ref = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<"ask" | "here" | "joined">("ask");
  const [ticked, setTicked] = useState(false);
  const [missing, setMissing] = useState(false);
  const [sending, setSending] = useState(false);
  /* What joining actually took off this preview, which the server names: the
     card quotes today's offer, and an address that joined while it was 15%
     keeps its 15%. */
  const [got, setGot] = useState<number | null>(null);
  /* The offer as the server last made it: in the language the page is read
     in now, and still there once they have joined, when the re-read page no
     longer offers it and the thank-you still names the percent. */
  const kept = useRef(preview.newsletter ?? null);
  if (preview.newsletter) kept.current = preview.newsletter;
  const offer = kept.current;
  const asking = !!offer && !!page && stage === "ask";

  useEffect(() => {
    const node = ref.current;
    if (!asking || !node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "newsletter" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [asking]);

  if (!offer || !page) return null;
  const percent = stage === "here" && got ? got : offer.percent;
  /* Chinese says a discount as what is paid, in tenths: 15% off is 85 折,
     20% off is 8 折. */
  const pay = 100 - percent;
  const vars = { percent: String(percent), pay: String(pay % 10 === 0 ? pay / 10 : pay) };

  if (stage !== "ask") {
    return (
      // The 14px under it that the card it replaces had.
      <p className="notice notice-good" style={{ margin: "0 0 14px" }}>
        {stage === "here" ? t.t("nlDone", vars) : t.t("nlDoneOnly")}
      </p>
    );
  }

  const several = (preview.pieces?.length ?? 1) > 1;
  const full = preview.price?.full;

  async function join() {
    if (!page || page.admin) return;
    if (!ticked) {
      setMissing(true);
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: page.token,
          consent: true,
          consent_text: t.t("nlConsent"),
          language: page.language,
        }),
      });
      const data = (await r.json().catch(() => ({}))) as { ok?: boolean; here?: boolean; percent?: number };
      if (!r.ok || !data.ok) throw new Error(String(r.status));
      if (typeof data.percent === "number" && data.percent > 0) setGot(data.percent);
      setStage(data.here ? "here" : "joined");
      page.refresh();
      page.refreshOthers?.();
    } catch {
      toast(t.t("sendFailed"), "bad");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="card stack" ref={ref}>
      <h2>{t.t("nlTitle", vars)}</h2>
      <p className="small" style={{ margin: 0 }}>
        {several || !full ? t.t("nlTextAll") : t.t("nlText", { price: offer.from, full })}
      </p>
      <label className="check" style={{ alignItems: "flex-start" }}>
        <input
          type="checkbox"
          checked={ticked}
          onChange={(e) => {
            setTicked(e.target.checked);
            if (e.target.checked) setMissing(false);
          }}
        />
        <span className="small">
          {t.t("nlConsent")}{" "}
          <a
            href={siteUrl(page.language, "privacy.html", undefined, "newsletter")}
            target="_blank"
            rel="noopener"
          >
            {t.t("privacy")}
          </a>
        </span>
      </label>
      {missing && (
        <p className="small" role="alert" style={{ margin: 0, color: "var(--red)" }}>
          {t.t("nlMissing")}
        </p>
      )}
      <button
        className="btn btn-soft btn-block"
        disabled={sending || page.admin}
        onClick={() => void join()}
      >
        {sending ? <span className="spin" /> : t.t("nlButton", vars)}
      </button>
      <p className="tiny muted center" style={{ margin: 0 }}>
        {t.t("nlWho")}
      </p>
    </div>
  );
}
